// Everything the app does with the service worker, device storage and the
// install prompt. Keep the cache names in step with public/sw.js.

const STORY_CACHES = ["hv-stories-v1", "hv-media-v1"];
const UPDATE_EVENT = "hv:update-ready";
const FIRST_STORY_KEY = "hv.firstStoryDone";

// Captured at import time. index.js imports this module first, so the event
// is caught even when it fires before React has mounted anything.
let waitingWorker = null;
let userAcceptedUpdate = false;

// ── Service worker ──────────────────────────────────────────────────────────

// Production only. The dev server's bundles are not content-hashed, so a
// cache-first worker would keep serving stale code during development.
export function registerServiceWorker() {
  if (process.env.NODE_ENV !== "production" || !("serviceWorker" in navigator)) return;

  window.addEventListener("load", async () => {
    try {
      const reg = await navigator.serviceWorker.register("/sw.js");
      if (reg.waiting && navigator.serviceWorker.controller) announceUpdate(reg.waiting);

      reg.addEventListener("updatefound", () => {
        const next = reg.installing;
        next?.addEventListener("statechange", () => {
          if (next.state === "installed" && navigator.serviceWorker.controller) announceUpdate(next);
        });
      });

      // Look for a new build whenever the family comes back to the app.
      document.addEventListener("visibilitychange", () => {
        if (!document.hidden) reg.update().catch(() => {});
      });
    } catch {
      // Offline support is an enhancement. The app works without it.
    }
  });

  // Reload only after the family has asked for the update. The first install
  // also fires controllerchange, and that must not reload anything.
  let reloaded = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (userAcceptedUpdate && !reloaded) {
      reloaded = true;
      window.location.reload();
    }
  });
}

function announceUpdate(worker) {
  waitingWorker = worker;
  window.dispatchEvent(new Event(UPDATE_EVENT));
}

export function onUpdateReady(fn) {
  window.addEventListener(UPDATE_EVENT, fn);
  return () => window.removeEventListener(UPDATE_EVENT, fn);
}

export function hasWaitingUpdate() {
  return !!waitingWorker;
}

export function applyUpdate() {
  userAcceptedUpdate = true;
  waitingWorker?.postMessage({ type: "SKIP_WAITING" });
}

// ── Storage ─────────────────────────────────────────────────────────────────

// Asks the browser not to evict downloaded stories under storage pressure.
export async function requestPersistentStorage() {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export async function getStorageUsage() {
  const est = (await navigator.storage?.estimate?.()) || {};
  const persisted = (await navigator.storage?.persisted?.()) ?? false;
  return { usage: est.usage || 0, quota: est.quota || 0, persisted };
}

// Removes downloaded stories and media, and nothing else. The app shell and
// the account itself are left alone.
export async function clearDownloads() {
  await Promise.all(STORY_CACHES.map((name) => caches.delete(name)));
}

// Sign-out: nothing from the signed-out account is left behind for the next
// person on this device. Shell caches stay, because they hold no account data.
export async function clearAccountCaches() {
  if (!("caches" in window)) return;
  await Promise.all(STORY_CACHES.map((name) => caches.delete(name)));
}

// ── Offline download ────────────────────────────────────────────────────────

// Image URLs for one story, matching what StoryDetail actually displays. Video
// is left out on purpose.
export function storyMediaUrls(story, seq) {
  const urls = [];
  if (story?.cover_image_gridfs_id) urls.push(`/api/stories/${seq}/media/cover-image`);
  (story?.storybook || []).forEach((page, idx) => {
    if (page?.image_gridfs_id) urls.push(`/api/stories/${seq}/media/storybook/${idx}`);
  });
  return urls;
}

// Loads each URL through the signed-in client. The service worker caches each
// response on the way through. Returns the total bytes fetched.
export async function downloadMedia(urls, fetchOne, onProgress) {
  let bytes = 0;
  for (let i = 0; i < urls.length; i++) {
    bytes += await fetchOne(urls[i]);
    onProgress?.(i + 1, urls.length, bytes);
  }
  return bytes;
}

// ── First finished story ────────────────────────────────────────────────────

// Called after a family's first completed story. It asks the browser to keep
// downloaded stories safe. The install offer does not wait for this.
export function markFirstStoryDone() {
  try { localStorage.setItem(FIRST_STORY_KEY, "1"); } catch { /* ignore */ }
  requestPersistentStorage();
}
